import {Directive, ElementRef, inject, OnInit, Renderer2} from '@angular/core';
import {AbstractControl, NgControl} from '@angular/forms';
import {Subject, takeUntil} from 'rxjs';

@Directive({
    selector:
        '[appInputRequirementIndicator], input, textarea, p-select, p-treeSelect, p-inputnumber, [pInputText], p-password',
    exportAs: 'appInputRequirementIndicator'
})
export class InputRequirementIndicatorDirective implements OnInit {
    private readonly _destroy$ = new Subject<void>();
    private readonly _element = inject<ElementRef>(ElementRef);
    private readonly _renderer = inject(Renderer2);
    private readonly _ngControl = inject<NgControl>(NgControl, {
        optional: true,
        self: true
    });

    // ===============================================================
    // ===============================================================

    /**
     * On init
     */
    public ngOnInit() {
        if (!this._ngControl?.control) {
            console.warn(
                '[InputRequirementIndicatorDirective] No FormControl found on host element. Directive ignored.'
            );
            return;
        }

        const labelEl = this.findLabel(this._element.nativeElement);
        if (!labelEl) {
            console.warn(
                '[InputRequirementIndicatorDirective] No <label> found for the control. Provide a <label for="…"> element.'
            );
            return;
        }

        this.applyAsteriskIfRequired(labelEl, this._ngControl.control);

        // Subscribe to status changes (required validator can change at runtime)
        this._ngControl.control.statusChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this.applyAsteriskIfRequired(labelEl, this._ngControl!.control!));
    }
    /**
     * On destroy
     */
    public ngOnDestry(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Apply the asterisk if need, or restore the original label
     * @param labelElement Target element
     * @param control Target control
     */
    private applyAsteriskIfRequired(labelElement: HTMLElement, control: AbstractControl): void {
        const isReq = this.isRequired(control);

        if (isReq) {
            this._renderer.setAttribute(labelElement, 'data-required', 'true');
        } else {
            this._renderer.removeAttribute(labelElement, 'data-required');
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * Detects if the supplied control contains the required validator.
     * Handles both single validator functions and validator groups.
     */
    private isRequired(control: AbstractControl): boolean {
        const validatorFn = control.validator;
        if (!validatorFn) {
            return false;
        }

        // Create a fake control to run the validator on an empty value.
        // If it returns an object with a `required` key, the validator is present.
        const errors = validatorFn({value: ''} as AbstractControl);
        return !!errors?.['required'];
    }
    /**
     * Tries to locate the label for the control.
     * Supports both <label for="…"> and PrimeNG <p-floatlabel> (label inside the wrapper).
     */
    private findLabel(controlEl: HTMLElement): HTMLElement | null {
        const id = controlEl.id;

        // Case A – normal <label for="…">
        if (id) {
            const label = controlEl.ownerDocument.querySelector(`label[for="${id}"]`);
            if (label && label instanceof HTMLElement) {
                return label;
            }
        }

        // Case B – PrimeNG <p-floatlabel> wrapper
        // The label is the last child of the wrapper
        const wrapper = controlEl.closest('[p-floatlabel], p-floatlabel');
        if (wrapper) {
            const label = wrapper.querySelector('label');
            if (label && label instanceof HTMLElement) {
                return label;
            }
        }

        return null; // no label found
    }
}
